import React, { useState, useCallback } from 'react';
import { User } from 'firebase/auth';
import type { Department, Shift, PickupLocation, ReservationSource, DayOfWeek, AdminUser, LoginCredentials, StaffMember, WorkloadFactors, AttendanceRequest } from '../../types';
import { ALL_DAYS } from '../../types';
import { Icons } from '../ui/Icons';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import ConfirmationModal from '../ui/ConfirmationModal';
import AdminManagementView from '../admin/AdminManagementView';
import TaskConfigurationView from './TaskConfigurationView';
import MemoManagementView from './MemoManagementView';

interface SettingsViewProps {
    // Core Settings
    departments: Department[];
    shifts: Shift[];
    pickupLocations: PickupLocation[];
    reservationSources: ReservationSource[];
    weekStartDay: DayOfWeek;
    propertyName: string;
    parkingCapacity: number;
    attendanceRequests: AttendanceRequest[];
    onSaveDepartment: (department: Omit<Department, 'id'> & { id?: string }) => void;
    onDeleteDepartment: (id: string) => void;
    onSaveShift: (shift: Omit<Shift, 'id'> & { id?: string }) => void;
    onDeleteShift: (id: string) => void;
    onSavePickupLocation: (location: Omit<PickupLocation, 'id'> & { id?: string }) => void;
    onDeletePickupLocation: (id: string) => void;
    onSaveReservationSource: (source: Omit<ReservationSource, 'id'> & { id?: string }) => void;
    onDeleteReservationSource: (id: string) => void;
    onUpdateWeekStartDay: (day: DayOfWeek) => void;
    onUpdatePropertyName: (name: string) => void;
    onUpdateParkingCapacity: (capacity: number) => void;
    showLaborFactors?: boolean;
    onUpdateShowLaborFactors?: (show: boolean) => void;
    onUpdateDepartmentsOrder: (departments: Department[]) => void;
    workloadFactors: WorkloadFactors;
    onUpdateWorkloadFactors: (factors: WorkloadFactors) => void;

    // Admin Management (Moved into Settings)
    currentUser: User | null;
    adminUsers: AdminUser[];
    onCreateAdmin: (credentials: LoginCredentials) => Promise<void>;
    onDeleteCurrentUser: () => Promise<void>;
    onUpdateAdminStatus: (uid: string, status: 'active' | 'suspended') => Promise<void>;
    onDeleteAdmin: (uid: string, email: string) => Promise<void>;
    onSaveAdmin: (admin: AdminUser) => Promise<void>;
    onReviewAttendanceRequest: (requestId: string, status: 'APPROVED' | 'REJECTED', adminNote?: string) => void;
    staffList: StaffMember[];
}

type SettingsTab = 'general' | 'departments' | 'shifts' | 'locations' | 'sources' | 'admins' | 'tasks' | 'memos';

const SettingsView: React.FC<SettingsViewProps> = ({ 
    departments, shifts, pickupLocations, reservationSources, weekStartDay, propertyName, parkingCapacity, attendanceRequests = [],
    showLaborFactors, onUpdateShowLaborFactors,
    onSaveDepartment, onDeleteDepartment, 
    onSaveShift, onDeleteShift, 
    onSavePickupLocation, onDeletePickupLocation, 
    onSaveReservationSource, onDeleteReservationSource,
    onUpdateWeekStartDay, onUpdatePropertyName, onUpdateParkingCapacity, onUpdateDepartmentsOrder,
    currentUser, adminUsers, onCreateAdmin, onDeleteCurrentUser, onUpdateAdminStatus, onDeleteAdmin, onSaveAdmin,
    onReviewAttendanceRequest, staffList,
    workloadFactors, onUpdateWorkloadFactors
}) => {
    const [activeTab, setActiveTab] = useState<SettingsTab>('general');
    
    // Department State
    const [depName, setDepName] = useState('');
    const [depAbbreviation, setDepAbbreviation] = useState('');
    const [depColor, setDepColor] = useState('#3b82f6');
    const [editingDep, setEditingDep] = useState<Department | null>(null);

    // Shift State
    const [shiftName, setShiftName] = useState('');
    const [shiftStartTime, setShiftStartTime] = useState('09:00');
    const [shiftEndTime, setShiftEndTime] = useState('17:00');
    const [shiftColor, setShiftColor] = useState('#34d399');
    const [editingShift, setEditingShift] = useState<Shift | null>(null);

    // Pickup Location State
    const [locationName, setLocationName] = useState('');
    const [locationAddress, setLocationAddress] = useState('');
    const [locationLat, setLocationLat] = useState<number>(0);
    const [locationLng, setLocationLng] = useState<number>(0);
    const [locationColor, setLocationColor] = useState('#f59e0b');
    const [editingLocation, setEditingLocation] = useState<PickupLocation | null>(null);

    // Reservation Source State
    const [sourceName, setSourceName] = useState('');
    const [editingSource, setEditingSource] = useState<ReservationSource | null>(null);

    const departmentDeleteHandler = useDeleteHandler<Department>({
        onDeleteAsync: async (dep) => onDeleteDepartment(dep.id),
        entityName: 'Department',
        getDisplayName: (dep) => dep.name,
        getItemId: (dep) => dep.id,
    });

    const shiftDeleteHandler = useDeleteHandler<Shift>({
        onDeleteAsync: async (shift) => onDeleteShift(shift.id),
        entityName: 'Shift',
        getDisplayName: (shift) => shift.name,
        getItemId: (shift) => shift.id,
    });

    const locationDeleteHandler = useDeleteHandler<PickupLocation>({
        onDeleteAsync: async (location) => onDeletePickupLocation(location.id),
        entityName: 'Pickup Location',
        getDisplayName: (location) => location.name,
        getItemId: (location) => location.id,
    });

    const sourceDeleteHandler = useDeleteHandler<ReservationSource>({
        onDeleteAsync: async (source) => onDeleteReservationSource(source.id),
        entityName: 'Reservation Source',
        getDisplayName: (source) => source.name,
        getItemId: (source) => source.id,
    });

    const handleSaveDepartment = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!depName) return;

        const departmentData = { 
            name: depName, 
            abbreviation: depAbbreviation.toUpperCase(), 
            color: depColor 
        };
        if (editingDep) {
            onSaveDepartment({ ...departmentData, id: editingDep.id });
        } else {
            onSaveDepartment(departmentData);
        }

        setEditingDep(null);
        setDepName('');
        setDepAbbreviation('');
        setDepColor('#3b82f6');
    }, [depName, depAbbreviation, depColor, editingDep, onSaveDepartment]);

    const handleEditDepartment = useCallback((dep: Department) => {
        setEditingDep(dep);
        setDepName(dep.name);
        setDepAbbreviation(dep.abbreviation || '');
        setDepColor(dep.color);
    }, []);

    const handleSaveShift = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!shiftName) return;
        
        const shiftData = { name: shiftName, startTime: shiftStartTime, endTime: shiftEndTime, color: shiftColor };
        if (editingShift) {
            onSaveShift({ ...shiftData, id: editingShift.id });
        } else {
            onSaveShift(shiftData);
        }

        setEditingShift(null);
        setShiftName('');
        setShiftStartTime('09:00');
        setShiftEndTime('17:00');
        setShiftColor('#34d399');
    }, [shiftName, shiftStartTime, shiftEndTime, shiftColor, editingShift, onSaveShift]);

    const handleEditShift = useCallback((shift: Shift) => {
        setEditingShift(shift);
        setShiftName(shift.name);
        setShiftStartTime(shift.startTime);
        setShiftEndTime(shift.endTime);
        setShiftColor(shift.color);
    }, []);

    const handleCancelEditDepartment = useCallback(() => {
        setEditingDep(null);
        setDepName('');
        setDepAbbreviation('');
        setDepColor('#3b82f6');
    }, []);
    
    const handleCancelEditShift = useCallback(() => {
        setEditingShift(null);
        setShiftName('');
        setShiftStartTime('09:00');
        setShiftEndTime('17:00');
        setShiftColor('#34d399');
    }, []);

    const handleSavePickupLocation = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!locationName) return;

        const locationData = { name: locationName, address: locationAddress, latitude: locationLat, longitude: locationLng, color: locationColor };
        if (editingLocation) {
            onSavePickupLocation({ ...locationData, id: editingLocation.id });
        } else {
            onSavePickupLocation(locationData);
        }

        setEditingLocation(null);
        setLocationName('');
        setLocationAddress('');
        setLocationLat(0);
        setLocationLng(0);
        setLocationColor('#f59e0b');
    }, [locationName, locationAddress, locationLat, locationLng, locationColor, editingLocation, onSavePickupLocation]);

    const handleEditPickupLocation = useCallback((location: PickupLocation) => {
        setEditingLocation(location);
        setLocationName(location.name);
        setLocationAddress(location.address);
        setLocationLat(location.latitude);
        setLocationLng(location.longitude);
        setLocationColor(location.color);
    }, []);

    const handleCancelEditPickupLocation = useCallback(() => {
        setEditingLocation(null);
        setLocationName('');
        setLocationAddress('');
        setLocationLat(0);
        setLocationLng(0);
        setLocationColor('#f59e0b');
    }, []);

    const handleSaveReservationSource = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!sourceName) return;

        const sourceData = { name: sourceName };
        if (editingSource) {
            onSaveReservationSource({ ...sourceData, id: editingSource.id });
        } else {
            onSaveReservationSource(sourceData);
        }

        setEditingSource(null);
        setSourceName('');
    }, [sourceName, editingSource, onSaveReservationSource]);

    const handleEditReservationSource = useCallback((source: ReservationSource) => {
        setEditingSource(source);
        setSourceName(source.name);
    }, []);

    const handleCancelEditReservationSource = useCallback(() => {
        setEditingSource(null);
        setSourceName('');
    }, []);

    const moveDepartment = useCallback((index: number, direction: 'up' | 'down') => {
        const newDepartments = [...departments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        
        if (targetIndex < 0 || targetIndex >= newDepartments.length) return;
        
        const temp = newDepartments[index];
        newDepartments[index] = newDepartments[targetIndex];
        newDepartments[targetIndex] = temp;
        
        // Update order values
        const updatedDepartments = newDepartments.map((dep, i) => ({
            ...dep,
            order: i
        }));
        
        onUpdateDepartmentsOrder(updatedDepartments);
    }, [departments, onUpdateDepartmentsOrder]);

    const handleDepNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setDepName(e.target.value);
    const handleDepColorChange = (e: React.ChangeEvent<HTMLInputElement>) => setDepColor(e.target.value);
    const handleShiftNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setShiftName(e.target.value);
    const handleShiftStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => setShiftStartTime(e.target.value);
    const handleShiftEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => setShiftEndTime(e.target.value);
    const handleShiftColorChange = (e: React.ChangeEvent<HTMLInputElement>) => setShiftColor(e.target.value);

    const handleLocationNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocationName(e.target.value);
    const handleLocationAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocationAddress(e.target.value);
    const handleLocationLatChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocationLat(parseFloat(e.target.value));
    const handleLocationLngChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocationLng(parseFloat(e.target.value));
    const handleLocationColorChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocationColor(e.target.value);

    const handleSourceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setSourceName(e.target.value);

    const tabs: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
        { id: 'general', label: 'General', icon: <Icons.Settings className="w-5 h-5" /> },
        { id: 'departments', label: 'Departments', icon: <Icons.Users className="w-5 h-5" /> },
        { id: 'shifts', label: 'Shifts', icon: <Icons.Clock className="w-5 h-5" /> },
        { id: 'locations', label: 'Pickup Locations', icon: <Icons.MapPin className="w-5 h-5" /> },
        { id: 'sources', label: 'Reservation Sources', icon: <Icons.List className="w-5 h-5" /> },
        { id: 'tasks', label: 'Tasks', icon: <Icons.ClipboardCheck className="w-5 h-5" /> },
        { id: 'memos', label: 'Memo Management', icon: <Icons.Newspaper className="w-5 h-5" /> },
        { id: 'admins', label: 'Admins', icon: <Icons.Shield className="w-5 h-5" /> },
    ];

    const handleToggleMetrics = useCallback(() => {
        if (onUpdateShowLaborFactors) {
            onUpdateShowLaborFactors(!showLaborFactors);
        }
    }, [showLaborFactors, onUpdateShowLaborFactors]);

    return (
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:w-64 flex-shrink-0">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 dark:border-slate-700">
                        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">Settings</h2>
                    </div>
                    <nav className="p-2 space-y-1">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                                    activeTab === tab.id
                                        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700/50 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1">
                {activeTab === 'general' && (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">General Settings</h2>
                            <p className="text-gray-500 dark:text-gray-400">Configure global application preferences.</p>
                        </div>
                        
                        <div className="max-w-md space-y-6">
                            <div>
                                <label htmlFor="propertyName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    Property Name
                                </label>
                                <input
                                    type="text"
                                    id="propertyName"
                                    value={propertyName}
                                    onChange={(e) => onUpdatePropertyName(e.target.value)}
                                    placeholder="e.g. Hotel Operations Hub"
                                    className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                />
                                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                    This name will be displayed on the login page and in the application header.
                                </p>
                            </div>

                            <div>
                                <label htmlFor="parkingCapacity" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    Parking Capacity
                                </label>
                                <input
                                    type="number"
                                    id="parkingCapacity"
                                    value={parkingCapacity}
                                    onChange={(e) => onUpdateParkingCapacity(parseInt(e.target.value) || 0)}
                                    placeholder="e.g. 100"
                                    className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                />
                                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                    Total number of parking spots available at the property.
                                </p>
                            </div>

                            <div>
                                <label htmlFor="weekStartDay" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    Week Start Day
                                </label>
                                <select
                                    id="weekStartDay"
                                    value={weekStartDay}
                                    onChange={(e) => onUpdateWeekStartDay(e.target.value as DayOfWeek)}
                                    className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                >
                                    {ALL_DAYS.map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                    This determines the first day of the week in the scheduler, calendars, and reporting views.
                                </p>
                            </div>

                            <div className="pt-6 border-t border-gray-100 dark:border-slate-700">
                                <div className="mb-4">
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Display Settings</h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Customize the visibility of schedule components.</p>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/30 rounded-xl border border-gray-200 dark:border-slate-700">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Show Labor Factors</span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Toggle visibility of arrivals, departures, stay-overs, and workload metrics in the scheduler.</span>
                                    </div>
                                    <button 
                                        onClick={handleToggleMetrics}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                                            showLaborFactors ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-slate-600'
                                        }`}
                                    >
                                        <span className="sr-only">Toggle labor factors</span>
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                showLaborFactors ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-gray-100 dark:border-slate-700">
                                <div className="mb-4">
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Workload Calibration</h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Configure time-based factors for workload ratio calculation.</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Arrival Time (Minutes)</label>
                                        <input
                                            type="number"
                                            value={workloadFactors.arrivalTime}
                                            onChange={(e) => onUpdateWorkloadFactors({ ...workloadFactors, arrivalTime: parseInt(e.target.value) || 0 })}
                                            className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Departure Time (Minutes)</label>
                                        <input
                                            type="number"
                                            value={workloadFactors.departureTime}
                                            onChange={(e) => onUpdateWorkloadFactors({ ...workloadFactors, departureTime: parseInt(e.target.value) || 0 })}
                                            className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Stayover Time (Minutes)</label>
                                        <input
                                            type="number"
                                            value={workloadFactors.stayoverTime}
                                            onChange={(e) => onUpdateWorkloadFactors({ ...workloadFactors, stayoverTime: parseInt(e.target.value) || 0 })}
                                            className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Buffer Multiplier</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={workloadFactors.bufferMultiplier}
                                            onChange={(e) => onUpdateWorkloadFactors({ ...workloadFactors, bufferMultiplier: parseFloat(e.target.value) || 1 })}
                                            className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Workload Factor (e.g., 0.30 for +30%)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={workloadFactors.workloadFactor}
                                            onChange={(e) => onUpdateWorkloadFactors({ ...workloadFactors, workloadFactor: parseFloat(e.target.value) || 0 })}
                                            className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500"
                                        />
                                        <p className="text-[10px] text-gray-400 italic">Adds extra workload to account for calls, emails, reservations, and admin tasks</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'departments' && (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Manage Departments</h2>
                            <p className="text-gray-500 dark:text-gray-400">Create and organize departments for your staff.</p>
                        </div>

                        <form onSubmit={handleSaveDepartment} className="bg-gray-50 dark:bg-slate-900/50 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                                {editingDep ? 'Edit Department' : 'Add New Department'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-end">
                                <div className="space-y-2">
                                    <label htmlFor="depName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department Name</label>
                                    <input type="text" id="depName" value={depName} onChange={handleDepNameChange}
                                        className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="depAbbreviation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Abbreviation (e.g. MG)</label>
                                    <input type="text" id="depAbbreviation" value={depAbbreviation} onChange={(e) => setDepAbbreviation(e.target.value)}
                                        className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" maxLength={4} required />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="depColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Brand Color</label>
                                    <div className="flex gap-4">
                                        <input type="color" id="depColor" value={depColor} onChange={handleDepColorChange}
                                            className="block w-16 h-10 border border-gray-300 dark:border-slate-600 rounded-lg shadow-sm cursor-pointer" />
                                        <div className="flex-1 flex gap-2">
                                            <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                                                {editingDep ? 'Update' : 'Add'}
                                            </button>
                                            {editingDep && (
                                                <button type="button" onClick={handleCancelEditDepartment} className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {[...departments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((dep, index, sortedDeps) => (
                                <div key={dep.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:shadow-md transition-all group">
                                    <div className="flex items-center gap-4">
                                        <div className="flex flex-col gap-1">
                                            <button 
                                                onClick={() => moveDepartment(index, 'up')}
                                                disabled={index === 0}
                                                className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-0 transition-all"
                                            >
                                                <Icons.ChevronUp className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => moveDepartment(index, 'down')}
                                                disabled={index === sortedDeps.length - 1}
                                                className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-0 transition-all"
                                            >
                                                <Icons.ChevronDown className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-inner" style={{ backgroundColor: dep.color }}>
                                            <Icons.Users className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-900 dark:text-white">{dep.name} <span className="ml-2 text-xs font-black px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 rounded">{dep.abbreviation}</span></p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest">{dep.color}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEditDepartment(dep)} className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                                            <Icons.Edit className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => departmentDeleteHandler.initiateDelete(dep)} className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                            <Icons.Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'shifts' && (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Manage Shifts</h2>
                            <p className="text-gray-500 dark:text-gray-400">Define work shifts and their time ranges.</p>
                        </div>

                        <form onSubmit={handleSaveShift} className="bg-gray-50 dark:bg-slate-900/50 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                                {editingShift ? 'Edit Shift' : 'Add New Shift'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="shiftName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Shift Name</label>
                                    <input type="text" id="shiftName" value={shiftName} onChange={handleShiftNameChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="shiftStartTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start Time</label>
                                    <input type="time" id="shiftStartTime" value={shiftStartTime} onChange={handleShiftStartTimeChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="shiftEndTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300">End Time</label>
                                    <input type="time" id="shiftEndTime" value={shiftEndTime} onChange={handleShiftEndTimeChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="shiftColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Shift Color</label>
                                    <div className="flex gap-4">
                                        <input type="color" id="shiftColor" value={shiftColor} onChange={handleShiftColorChange}
                                            className="block w-16 h-10 border border-gray-300 dark:border-slate-600 rounded-lg shadow-sm cursor-pointer" />
                                        <div className="flex-1 flex gap-2">
                                            <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                                                {editingShift ? 'Update' : 'Add'}
                                            </button>
                                            {editingShift && (
                                                <button type="button" onClick={handleCancelEditShift} className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        {attendanceRequests.filter(tu => tu.status === 'PENDING').length > 0 && (
                            <div className="mb-8 p-6 bg-amber-50/50 dark:bg-amber-900/10 rounded-2xl border border-amber-100 dark:border-amber-900/30">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                    <Icons.Clock className="w-5 h-5 text-amber-500" />
                                    Review Pending Time Edits
                                </h3>
                                <div className="space-y-3">
                                    {attendanceRequests.filter(tu => tu.status === 'PENDING').map(update => {
                                        const employee = staffList.find(s => s.id === update.employeeId);
                                        const shift = shifts.find(s => s.id === update.shiftId);
                                        return (
                                            <div key={update.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 border border-amber-100 dark:border-amber-900/30 rounded-xl shadow-sm">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-bold text-gray-900 dark:text-white">{employee?.name || 'Unknown Employee'}</p>
                                                        <span className="text-xs px-2 py-0.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full font-bold uppercase tracking-wider">
                                                            {shift?.name || 'Manual Entry'}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-1">
                                                        Shift Date: {new Date(update.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                                                    </p>
                                                     <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300 mt-3 bg-gray-50 dark:bg-slate-900/50 p-2 rounded-lg">
                                                        <div className="text-center">
                                                            <p className="text-[9px] uppercase font-bold text-gray-400">Scheduled</p>
                                                            <p className="font-mono text-gray-500">{update.originalStartTime} - {update.originalEndTime}</p>
                                                        </div>
                                                        <Icons.ArrowRight className="w-4 h-4 text-indigo-400" />
                                                        <div className="text-center">
                                                            <p className="text-[9px] uppercase font-bold text-indigo-500">Requested</p>
                                                            <p className="font-mono font-bold text-indigo-600 dark:text-indigo-400">{update.requestedStartTime} - {update.requestedEndTime}</p>
                                                        </div>
                                                    </div>
                                                    {update.staffNote && (
                                                        <div className="mt-3 p-2 bg-indigo-50/30 dark:bg-indigo-900/10 rounded-lg border border-indigo-100/50 dark:border-indigo-900/20">
                                                            <p className="text-[9px] font-bold uppercase text-indigo-500 mb-1">Staff Note:</p>
                                                            <p className="text-xs text-gray-600 dark:text-gray-400 italic">"{update.staffNote}"</p>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <button 
                                                        onClick={() => onReviewAttendanceRequest(update.id, 'APPROVED')} 
                                                        className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 transition-all shadow-sm shadow-emerald-500/20 active:scale-95"
                                                    >
                                                        Approve
                                                    </button>
                                                    <button 
                                                        onClick={() => onReviewAttendanceRequest(update.id, 'REJECTED')} 
                                                        className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-red-600 dark:text-red-400 text-xs font-bold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all active:scale-95"
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="space-y-3">
                            {shifts.map(shift => (
                                <div key={shift.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:shadow-md transition-all group">
                                    <div className="flex items-center gap-4">
                                        <div className="w-3 h-12 rounded-full" style={{ backgroundColor: shift.color }}></div>
                                        <div>
                                            <p className="font-bold text-gray-900 dark:text-white">{shift.name}</p>
                                            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                                <Icons.Clock className="w-3.5 h-3.5" />
                                                <span>{shift.startTime} — {shift.endTime}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEditShift(shift)} className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                                            <Icons.Edit className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => shiftDeleteHandler.initiateDelete(shift)} className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                            <Icons.Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'locations' && (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Pickup Locations</h2>
                            <p className="text-gray-500 dark:text-gray-400">Manage shuttle pickup and drop-off points.</p>
                        </div>

                        <form onSubmit={handleSavePickupLocation} className="bg-gray-50 dark:bg-slate-900/50 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                                {editingLocation ? 'Edit Location' : 'Add New Location'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="locationName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Location Name</label>
                                    <input type="text" id="locationName" value={locationName} onChange={handleLocationNameChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="locationAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
                                    <input type="text" id="locationAddress" value={locationAddress} onChange={handleLocationAddressChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="locationLat" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Latitude</label>
                                    <input type="number" step="any" id="locationLat" value={locationLat} onChange={handleLocationLatChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="locationLng" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Longitude</label>
                                    <input type="number" step="any" id="locationLng" value={locationLng} onChange={handleLocationLngChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                </div>
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="locationColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Marker Color</label>
                                    <div className="flex gap-4">
                                        <input type="color" id="locationColor" value={locationColor} onChange={handleLocationColorChange}
                                            className="block w-16 h-10 border border-gray-300 dark:border-slate-600 rounded-lg shadow-sm cursor-pointer" />
                                        <div className="flex-1 flex gap-2">
                                            <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                                                {editingLocation ? 'Update' : 'Add'}
                                            </button>
                                            {editingLocation && (
                                                <button type="button" onClick={handleCancelEditPickupLocation} className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {pickupLocations.map(location => (
                                <div key={location.id} className="p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:shadow-md transition-all group relative">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-sm flex-shrink-0" style={{ backgroundColor: location.color }}>
                                            <Icons.MapPin className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-gray-900 dark:text-white truncate">{location.name}</p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{location.address || 'No address provided'}</p>
                                            <div className="mt-2 flex gap-3 text-[10px] font-mono text-gray-400 uppercase tracking-tighter">
                                                <span>Lat: {Number(location.latitude ?? 0).toFixed(4)}</span>
                                                <span>Lng: {Number(location.longitude ?? 0).toFixed(4)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEditPickupLocation(location)} className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                                            <Icons.Edit className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => locationDeleteHandler.initiateDelete(location)} className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                            <Icons.Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'sources' && (
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Reservation Sources</h2>
                            <p className="text-gray-500 dark:text-gray-400">Manage sources for parking reservations.</p>
                        </div>

                        <form onSubmit={handleSaveReservationSource} className="bg-gray-50 dark:bg-slate-900/50 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                                {editingSource ? 'Edit Source' : 'Add New Source'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="sm:col-span-2 space-y-2">
                                    <label htmlFor="sourceName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Source Name</label>
                                    <input type="text" id="sourceName" value={sourceName} onChange={handleSourceNameChange} className="block w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-lg shadow-sm py-2 px-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" required />
                                </div>
                                <div className="sm:col-span-2">
                                    <div className="flex justify-end gap-3">
                                        <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow-sm transition-colors">
                                            {editingSource ? 'Update' : 'Add'}
                                        </button>
                                        {editingSource && (
                                            <button type="button" onClick={handleCancelEditReservationSource} className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {reservationSources.map(source => (
                                <div key={source.id} className="p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:shadow-md transition-all group relative">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 shadow-sm flex-shrink-0">
                                            <Icons.List className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-gray-900 dark:text-white truncate">{source.name}</p>
                                        </div>
                                    </div>
                                    <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEditReservationSource(source)} className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                                            <Icons.Edit className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => sourceDeleteHandler.initiateDelete(source)} className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                            <Icons.Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'tasks' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 h-full">
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 ml-4">Task Management</h2>
                            <p className="text-gray-500 dark:text-gray-400 ml-4">Define blueprints and templates for daily operational tasks.</p>
                        </div>
                        <TaskConfigurationView isAdmin={adminUsers.some(a => a.uid === currentUser?.uid && (a.role === 'Super Admin' || a.role === 'Admin' || a.role === 'Administrator' || (a as any).type === 'admin'))} />
                    </div>
                )}

                {activeTab === 'memos' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <MemoManagementView 
                            isAdmin={adminUsers.some(a => a.uid === currentUser?.uid && (a.role === 'Super Admin' || a.role === 'Admin' || a.role === 'Administrator' || (a as any).type === 'admin'))}
                            staffId={currentUser?.uid || ''}
                            staffName={currentUser?.displayName || 'Admin'}
                        />
                    </div>
                )}

                {activeTab === 'admins' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <AdminManagementView 
                            currentUser={currentUser}
                            adminUsers={adminUsers}
                            departments={departments}
                            onCreateAdmin={onCreateAdmin}
                            onDeleteCurrentUser={onDeleteCurrentUser}
                            onUpdateAdminStatus={onUpdateAdminStatus}
                            onDeleteAdmin={onDeleteAdmin}
                            onSaveAdmin={onSaveAdmin}
                            weekStartDay={weekStartDay}
                        />
                    </div>
                )}
            </div>

            {/* Modals */}
            {departmentDeleteHandler.itemToDelete && (
                <ConfirmationModal
                    title="Delete Department"
                    message={`Are you sure you want to delete the "${departmentDeleteHandler.itemToDelete.name}" department? This action cannot be undone.`}
                    confirmButtonText="Delete"
                    onConfirm={departmentDeleteHandler.confirmDelete}
                    onCancel={departmentDeleteHandler.cancelDelete}
                    isConfirming={departmentDeleteHandler.isDeleting}
                />
            )}
            {shiftDeleteHandler.itemToDelete && (
                <ConfirmationModal
                    title="Delete Shift"
                    message={`Are you sure you want to delete the "${shiftDeleteHandler.itemToDelete.name}" shift? This action cannot be undone.`}
                    confirmButtonText="Delete"
                    onConfirm={shiftDeleteHandler.confirmDelete}
                    onCancel={shiftDeleteHandler.cancelDelete}
                    isConfirming={shiftDeleteHandler.isDeleting}
                />
            )}
            {locationDeleteHandler.itemToDelete && (
                <ConfirmationModal
                    title="Delete Pickup Location"
                    message={`Are you sure you want to delete the "${locationDeleteHandler.itemToDelete.name}" location? This action cannot be undone.`}
                    confirmButtonText="Delete"
                    onConfirm={locationDeleteHandler.confirmDelete}
                    onCancel={locationDeleteHandler.cancelDelete}
                    isConfirming={locationDeleteHandler.isDeleting}
                />
            )}
            {sourceDeleteHandler.itemToDelete && (
                <ConfirmationModal
                    title="Delete Reservation Source"
                    message={`Are you sure you want to delete the "${sourceDeleteHandler.itemToDelete.name}" source? This action cannot be undone.`}
                    confirmButtonText="Delete"
                    onConfirm={sourceDeleteHandler.confirmDelete}
                    onCancel={sourceDeleteHandler.cancelDelete}
                    isConfirming={sourceDeleteHandler.isDeleting}
                />
            )}
        </div>
    );
};

export default SettingsView;
